import React, { useContext } from 'react';
import { CategoryInsightsSettingsContextProvider } from 'contexts/CategoryInsightsSettingsContext/CategoryInsightsContextProvider';
import { SpendBySubCategoryProvider } from 'contexts/CategoryDashboardCharts/SpendBySubCategoryProvider';
import { VendorsBySpendProvider } from 'contexts/CategoryDashboardCharts/VendorsBySpendProvider';
import { VendorSpendByCategoryProvider } from 'contexts/CategoryDashboardCharts/VendorSpendByCategoryProvider';
import { SpendByCountryProvider } from 'contexts/CategoryDashboardCharts/SpendByCountryProvider';
import { VendorParetoProvider } from 'contexts/CategoryDashboardCharts/VendorParetoProvider';
import { SupplierLandscapeProvider } from 'contexts/CategoryDashboardCharts/SupplierLandscapeProvider';
import { SpendByBusinessUnitProvider } from 'contexts/CategoryDashboardCharts/SpendByBusinessUnitProvider';
import { SpendByCostCenterProvider } from 'contexts/CategoryDashboardCharts/SpendByCostCenterProvider';
import { DaysPaidOutstandingProvider } from 'contexts/CategoryDashboardCharts/DaysPaidOutstandingProvider';
import { SpendOverTimeProvider } from 'contexts/CategoryDashboardCharts/SpendOverTimeProvider';
import { CustomChartProvider } from 'contexts/CategoryDashboardCharts/CustomChartProvider';
import { PaymentTermBySupplierProvider } from 'contexts/CategoryDashboardCharts/PaymentTermBySupplierProvider';
import { ContractCoveragePercentProvider } from 'contexts/CategoryDashboardCharts/ContractCoveragePercentProvider';
import { SupplierClassificationProvider } from 'contexts/CategoryDashboardCharts/SupplierClassificationProvider';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { getCurrencySymbolFromOptions } from 'CategoryManagement/utils';
import { SpendByYearProvider } from 'contexts/CategoryDashboardCharts/SpendByYearProvider';
/**
 * withCategoryManagementProviders HOC Component
 * @param {JSX} WrappedComponent - Accepts a component and gives a Wrapped componets with Context Providers
 */

// Higher Order Component (HOC) that wraps a component with multiple providers
const withCategoryManagementDashboardProviders = WrappedComponent => {
	// Return a new component with added context providers
	return function CategoryManagementWrapper(props) {
		const {
			options: { currency: currencyList },
		} = useContext(SettingsOptionsContext);
		const { currency } = useContext(QueryStringContext);
		const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
		const { hideDecimals } = useContext(SettingsContext);
		return (
			<>
				<CategoryInsightsSettingsContextProvider>
					<SpendBySubCategoryProvider
						currencySymbol={currencySymbol}
						hideDecimals={hideDecimals}
					>
						<VendorsBySpendProvider
							currencySymbol={currencySymbol}
							hideDecimals={hideDecimals}
						>
							<VendorSpendByCategoryProvider
								currencySymbol={currencySymbol}
								hideDecimals={hideDecimals}
							>
								<SpendByCountryProvider
									currencySymbol={currencySymbol}
									hideDecimals={hideDecimals}
								>
									<VendorParetoProvider
										currencySymbol={currencySymbol}
										hideDecimals={hideDecimals}
									>
										<SupplierLandscapeProvider>
											<SpendByBusinessUnitProvider
												currencySymbol={currencySymbol}
												hideDecimals={hideDecimals}
											>
												<SpendByCostCenterProvider
													currencySymbol={currencySymbol}
													hideDecimals={hideDecimals}
												>
													<DaysPaidOutstandingProvider
														currencySymbol={currencySymbol}
														hideDecimals={hideDecimals}
													>
														<SpendOverTimeProvider
															currencySymbol={currencySymbol}
															hideDecimals={hideDecimals}
														>
															<CustomChartProvider
																currencySymbol={currencySymbol}
																hideDecimals={hideDecimals}
															>
																<PaymentTermBySupplierProvider
																	currencySymbol={currencySymbol}
																	hideDecimals={hideDecimals}
																>
																	<ContractCoveragePercentProvider
																		currencySymbol={currencySymbol}
																		hideDecimals={hideDecimals}
																	>
																		<SupplierClassificationProvider
																			currencySymbol={currencySymbol}
																			hideDecimals={hideDecimals}
																		>
																			<SpendByYearProvider
																				currencySymbol={currencySymbol}
																				hideDecimals={hideDecimals}
																			>
																				<WrappedComponent {...props} />
																			</SpendByYearProvider>
																		</SupplierClassificationProvider>
																	</ContractCoveragePercentProvider>
																</PaymentTermBySupplierProvider>
															</CustomChartProvider>
														</SpendOverTimeProvider>
													</DaysPaidOutstandingProvider>
												</SpendByCostCenterProvider>
											</SpendByBusinessUnitProvider>
										</SupplierLandscapeProvider>
									</VendorParetoProvider>
								</SpendByCountryProvider>
							</VendorSpendByCategoryProvider>
						</VendorsBySpendProvider>
					</SpendBySubCategoryProvider>
				</CategoryInsightsSettingsContextProvider>
			</>
		);
	};
};

export default withCategoryManagementDashboardProviders;
